import { Badge, StatusBadge, Tag } from '@vrplatform/components';
import {
  useDashboard,
  usePropertyManagementConnectionsQuery,
  useQuery,
  useTeam,
} from '@vrplatform/data';
import {
  type MRT_ColumnDef,
  TableEntityValue,
  TableInlineAction,
} from '@vrplatform/table';
import { day, formatCurrency, hasValue, sortBy } from '@vrplatform/utils';
import {
  Group,
  HoverCard,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  rem,
  useMantineTheme,
} from '@mantine/core';
import {
  formatOwnerName,
  formatOwnershipBusinessModel,
  formatPercentage,
  pluralize,
} from '@vrplatform/ui-common';
import { useCallback, useMemo } from 'react';
import { useOwnerDetailDrawer } from '../../../../drawers';
import { useListingDetailDrawer } from '../../drawer';
import { getListingDeactivation } from '../../detail/useListingDetailViewQuery';
import { useListingsTableFilter } from './ListingsTableFilter';
import type { ListingRow } from './useListingsTableQuery';
import { useNavigate } from 'react-router';
import type { recurringFeeType_enum } from '@vrplatform/graphql';
import {
  getFeeTitleFromFeeTypeRoute,
  getFeeTypeRouteFromRecurringFeeEnum,
} from '../../../fees/overview/_utils';

function useGotoFeesOverview(listingId: string, type: recurringFeeType_enum) {
  const goto = useNavigate();

  return useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>
    ) => {
      event.stopPropagation();
      goto(
        `/listing/${listingId}/fees-commissions/${getFeeTypeRouteFromRecurringFeeEnum(type)}`
      );
    },
    [goto, listingId, type]
  );
}

type TableColumn = MRT_ColumnDef<ListingRow>;

export const useListingsTableColumns = () => {
  const [dashboard] = useDashboard();
  const isOwnerPortal = dashboard === 'owner';
  const { data: connections = [] } = usePropertyManagementConnectionsQuery();
  const { open: openListingDetailDrawer } = useListingDetailDrawer();

  const { filter } = useListingsTableFilter();

  const status = filter.status ?? 'active';
  const connectionsById = useMemo(
    () => new Map(connections.map((connection) => [connection.id, connection])),
    [connections]
  );
  const getConnection = useCallback(
    (connectionId: ListingRow['connectionId']) =>
      connectionId ? connectionsById.get(connectionId) : undefined,
    [connectionsById]
  );

  return useMemo(() => {
    const nameColumn: TableColumn = {
      accessorKey: 'name',
      enableColumnDragging: false,
      header: 'Name',
      size: 160,
      type: 'string',
    };

    const addressColumn: TableColumn = {
      accessorFn: (row) => row.address || null,
      enableColumnDragging: false,
      header: 'Address',
      id: 'address',
      size: 180,
      type: 'address',
    };

    const syncedFromColumn: TableColumn = {
      accessorFn: (row) => getConnection(row.connectionId)?.name || null,
      enableColumnDragging: false,
      header: 'Synced from',
      id: 'connection',
      size: 120,
      type: 'connection',
      typeProps: ({ row }) => ({
        logoUrl: getConnection(row.connectionId)?.logoUrl || null,
      }),
    };

    const businessModel: TableColumn = {
      enableColumnDragging: false,
      header: 'Business Model',
      size: 140,
      Cell: ({ row }) => {
        if (!row.original.activeOwnership?.businessModel) return '-';

        return (
          <StatusBadge
            label={formatOwnershipBusinessModel(
              row.original.activeOwnership?.businessModel
            )}
            status={
              row.original.activeOwnership.businessModel === 'managed'
                ? 'managed'
                : 'cohost'
            }
          />
        );
      },
    };

    const statusColumn: TableColumn = {
      enableColumnDragging: false,
      header: 'Status',
      size: 120,
      Cell: () => {
        const isInactive = status === 'inactive';

        return (
          <StatusBadge
            label={isInactive ? 'Inactive' : 'Active'}
            status={isInactive ? 'cancelled' : 'success'}
          />
        );
      },
    };

    // const collectionColumn: TableColumn =  {
    //         id: 'collectionId',
    //         header: 'Listing Group',
    //         minSize: 200,
    //         enableColumnDragging: false,
    //         getGroupingValue: (row) => row.collection?.id || '',
    //         enableGrouping: true,
    //         GroupedCell: ({ row }) => row.original.collection?.name,
    //         maxSize: 70,
    //         Cell: ({ row }) => {
    //           const collection = row.original.collection;
    //           const listingId = row.original.id;

    //           if (!listingId) return null;

    //           return (
    //             collection?.id && (
    //               <ListingCollectionSelect
    //                 collection={collection}
    //                 listingId={listingId}
    //               />
    //             )
    //           );
    //         },
    //         enableHiding: false,
    //       }

    const ownershipColumn: TableColumn = {
      accessorKey: 'ownerships',
      header: 'Ownership',
      enableSorting: false,
      enableColumnDragging: false,
      size: 140,
      Cell: ({ row }) => {
        const [{ id: teamId }] = useTeam();
        const { open: openOwnerDetailDrawer } = useOwnerDetailDrawer();
        const members = row.original.activeOwnership?.members || [];
        const contactIds = members.map((m) => m.contactId).filter(hasValue);

        const { data, isLoading, error } = useQuery(
          (q, args) => {
            const deactivation =
              args.status === 'active'
                ? undefined
                : getListingDeactivation(
                    q.listing({ id: args.listingId }),
                    args.today
                  );

            const contacts =
              args.status === 'inactive' || !args.contactIds.length
                ? []
                : q
                    .contacts({
                      where: {
                        id: {
                          _in: args.contactIds,
                        },
                      },
                      order_by: [{ name: 'asc_nulls_last' }],
                    })
                    .map((c) => ({
                      id: c.id,
                      name: formatOwnerName(c, {
                        lastNameFirst: false,
                      }),
                      type: c.companyType
                        ? ('company' as const)
                        : ('individual' as const),
                    }));

            return {
              contacts,
              deactivation,
            };
          },
          {
            skip: !row.original.id,
            queryKey: ['owners'],
            variables: {
              contactIds,
              teamId,
              status,
              listingId: row.original.id,
              today: day().yyyymmdd(),
            },
            keepPreviousData: true,
          }
        );

        if (error)
          return <Tag color="red">{error?.message || 'Failed to load'}</Tag>;

        if (isLoading) return <Skeleton height={20} width={150} />;

        if (status === 'inactive') {
          if (!data?.deactivation || data.deactivation.status === 'active')
            return '-';

          return (
            <Badge color="yellow">
              Deactivated from{' '}
              {day(data.deactivation.period.startAt).format('ll')}
              {data.deactivation.period.endAt
                ? ` until ${day(data.deactivation.period.endAt).format('ll')}`
                : ''}
            </Badge>
          );
        }

        if (!row.original.activeOwnership) {
          return (
            <TableInlineAction
              label="Add Ownership"
              onClick={(event) => {
                event.stopPropagation();
                openListingDetailDrawer(row.original.id, 'record-ownership');
              }}
            />
          );
        }

        return (
          <Group gap={rem(5)}>
            {members.map((member) => {
              const contact = data?.contacts?.find(
                (c) => c.id === member.contactId
              );

              if (!contact) return null;

              return (
                <button
                  key={contact.id}
                  onClick={(event) => {
                    event.stopPropagation();
                    openOwnerDetailDrawer(contact.id);
                  }}
                  style={{
                    background: 'transparent',
                    border: 0,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    padding: 0,
                  }}
                  type="button"
                >
                  <TableEntityValue
                    type="owner"
                    value={
                      member.split === 100_000
                        ? contact.name
                        : `${contact.name} (${formatPercentage(member.split ?? 0, 'super-base-points')})`
                    }
                  />
                </button>
              );
            })}
          </Group>
        );
      },
    };

    const mgtComColumns: TableColumn = {
      header: getFeeTitleFromFeeTypeRoute('commissions'),
      enableSorting: false,
      enableColumnDragging: false,
      minSize: 140,
      size: 140,
      Cell: ({ row }) => {
        const gotoOverview = useGotoFeesOverview(
          row.original.id,
          'managementFee'
        );

        const fees = row.original.activeRecurringFeePeriods?.filter(
          (x) => x.recurringFee.type === 'managementFee'
        );

        if (!fees?.length) {
          return <TableInlineAction label="Add fee" onClick={gotoOverview} />;
        }

        if (fees.length === 1) {
          return (
            <Badge
              appearance="tag"
              component="button"
              leftIcon="CircleDollarIcon"
              leftIconColor={(theme) => theme.colors[theme.primaryColor][6]}
              onClick={gotoOverview}
            >
              {formatPercentage(fees[0].rate ?? 0, 'super-base-points')}
            </Badge>
          );
        }

        return (
          <FeesDropdown
            listingId={row.original.id}
            type="managementFee"
            fees={fees}
          />
        );
      },
    };

    const getFeesColumn = (type: recurringFeeType_enum): TableColumn => {
      const route = getFeeTypeRouteFromRecurringFeeEnum(type);
      const title = getFeeTitleFromFeeTypeRoute(route);

      return {
        id: type,
        header: pluralize(2, title),
        enableSorting: false,
        enableColumnDragging: false,
        minSize: 120,
        size: 120,
        Cell: ({ row }) => {
          const gotoOverview = useGotoFeesOverview(row.original.id, type);
          const fees = row.original.activeRecurringFeePeriods?.filter(
            (x) => x.recurringFee.type === type
          );

          if (fees?.length) {
            return (
              <FeesDropdown
                listingId={row.original.id}
                type={type}
                fees={fees}
              />
            );
          }

          return <TableInlineAction label="Add fee" onClick={gotoOverview} />;
        },
      };
    };

    if (isOwnerPortal) {
      return [nameColumn];
    }

    return [
      nameColumn,
      addressColumn,
      syncedFromColumn,
      ownershipColumn,
      businessModel,
      statusColumn,
      mgtComColumns,
      getFeesColumn('bookingChannelFee'),
      getFeesColumn('merchantFee'),
      getFeesColumn('cleaningFee'),
      getFeesColumn('additionalFee'),
    ];
  }, [getConnection, isOwnerPortal, openListingDetailDrawer, status]);
};

const FeesDropdown = ({
  listingId,
  type,
  fees,
}: {
  listingId: string;
  type: recurringFeeType_enum;
  fees: NonNullable<ListingRow['activeRecurringFeePeriods']>;
}) => {
  const [{ currency }] = useTeam();
  const theme = useMantineTheme();
  const gotoOverview = useGotoFeesOverview(listingId, type);

  return (
    <HoverCard>
      <HoverCard.Target>
        <Badge
          appearance="tag"
          component="button"
          leftIcon="CircleDollarIcon"
          leftIconColor={theme.colors[theme.primaryColor][6]}
          onClick={gotoOverview}
        >
          {fees.length} {pluralize(fees.length, 'fee')}
        </Badge>
      </HoverCard.Target>
      <HoverCard.Dropdown
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <ScrollArea.Autosize mah={300}>
          <Stack>
            {sortBy(fees, (x) => x.recurringFee.name || '-').map((fee) => (
              <Group justify="space-between" key={fee.id} wrap="nowrap">
                <Text>{fee.recurringFee.name}</Text>
                <Text c="gray" ta="right">
                  {fee.recurringFee.rateType === 'flat'
                    ? formatCurrency((fee.rate ?? 0) / 100, currency)
                    : formatPercentage(fee.rate ?? 0, 'super-base-points')}
                </Text>
              </Group>
            ))}
          </Stack>
        </ScrollArea.Autosize>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};
